import { AlertTriangle, Info, Paintbrush, Wand2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeHex } from "@/shared/utils/color";
import {
  DEFAULT_THEME_ID,
  THEME_COLOR_KEYS,
  accentNeedsDarkerText,
  applyTheme,
  assessThemeColors,
  VAMOO_THEME_ID,
  VAMOO_UNLOCK_CODE,
  generateFromAccent,
  isVamooUnlockCode,
  isVamooUnlocked,
  previewCustomTheme,
  readThemeId,
  type ThemeColors
} from "@/shell/theme";
import { THEME_PRESETS, VAMOO_RAIL_IMAGE } from "./theme-presets";
import { ThemePreview } from "./ThemePreview";
import { Select } from "@/shared/components/Select";

const FIELDS: Record<keyof ThemeColors, { label: string; help: string }> = {
  rail: { label: "Menu e moldura", help: "Menu lateral, barra do topo e a borda em volta do conteúdo" },
  accent: { label: "Destaque", help: "Botões, item ativo do menu, links e seleção" },
  card1: { label: "Cartão de resumo 1", help: "Primeiro cartão do Início e do Dashboard" },
  card2: { label: "Cartão de resumo 2", help: "Segundo cartão do Início e do Dashboard" },
  card3: { label: "Cartão de resumo 3", help: "Terceiro cartão do Início e do Dashboard" }
};

/** Destaques prontos para um clique: matizes que não colidem com as
 *  cores de polaridade (verde/vermelho) — mesma regra dos temas prontos. */
const ACCENT_SUGGESTIONS = [
  { hex: "#305cff", name: "Azul elétrico" },
  { hex: "#4f46e5", name: "Índigo" },
  { hex: "#834dfb", name: "Violeta" },
  { hex: "#c026d3", name: "Magenta" },
  { hex: "#0e7490", name: "Petróleo" },
  { hex: "#0891b2", name: "Ciano" },
  { hex: "#ff5b04", name: "Laranja" },
  { hex: "#e0a100", name: "Âmbar" },
  { hex: "#3f4654", name: "Grafite" }
];

interface Props {
  initialColors: ThemeColors;
  /** O universo VAMOO já vem ligado (tema salvo com ele, ou o cartão pronto em uso). */
  initialVamoo: boolean;
  onSave: (colors: ThemeColors, vamoo: boolean) => void;
  onCancel: () => void;
  /** O código de desbloqueio do VAMOO foi digitado em "Menu e moldura"
   *  (`isVamooUnlockCode`): o editor liga o universo e segue aberto; a
   *  página só grava a marca de desbloqueio pro cartão pronto aparecer. */
  onUnlockCode: () => void;
}

/**
 * Editor do tema personalizado. Cada mudança pinta o app INTEIRO na hora
 * (`previewCustomTheme`) — o menu de verdade é a melhor pré-visualização
 * que existe. Nada vai pro storage até "Salvar"; ao sair do editor por
 * qualquer caminho (Descartar, trocar de aba, navegar) o tema salvo volta.
 */
export function CustomThemeEditor({ initialColors, initialVamoo, onSave, onCancel, onUnlockCode }: Props) {
  const [colors, setColors] = useState<ThemeColors>(initialColors);
  // O fundo VAMOO (tema oculto): ligado por `#fall` no campo do menu,
  // desligado por qualquer cor de verdade no mesmo campo. A cor do menu
  // continua valendo por baixo (moldura, bordas) — só o fundo e a logo mudam.
  const [vamoo, setVamoo] = useState(initialVamoo);
  // O que está escrito no campo hex — pode estar incompleto enquanto digita.
  const [drafts, setDrafts] = useState<ThemeColors>(() =>
    initialVamoo ? { ...initialColors, rail: VAMOO_UNLOCK_CODE } : initialColors
  );

  useEffect(() => {
    previewCustomTheme(colors, vamoo);
  }, [colors, vamoo]);

  useEffect(() => () => applyTheme(readThemeId() ?? DEFAULT_THEME_ID), []);

  // Abre lá embaixo da página (depois do modo de cor): leva o usuário até ele.
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  const warnings = useMemo(() => assessThemeColors(colors), [colors]);
  const darkerAccentText = accentNeedsDarkerText(colors.accent);

  const replaceAll = (next: ThemeColors, nextVamoo = false) => {
    setColors(next);
    setVamoo(nextVamoo);
    setDrafts(nextVamoo ? { ...next, rail: VAMOO_UNLOCK_CODE } : next);
  };

  const setColor = (key: keyof ThemeColors, value: string) => {
    if (key === "rail" && isVamooUnlockCode(value)) {
      setVamoo(true);
      setDrafts(prev => ({ ...prev, rail: VAMOO_UNLOCK_CODE }));
      onUnlockCode();
      return;
    }
    setDrafts(prev => ({ ...prev, [key]: value }));
    const hex = normalizeHex(value);
    if (hex) {
      setColors(prev => ({ ...prev, [key]: hex }));
      if (key === "rail") setVamoo(false);
    }
  };

  const startFrom = (presetId: string) => {
    const preset = presets.find(item => item.id === presetId);
    if (preset) replaceAll(preset.colors, preset.id === VAMOO_THEME_ID);
  };

  // O tema oculto só entra no "Começar de" de quem já destravou.
  const presets = THEME_PRESETS.filter(preset => !preset.secret || isVamooUnlocked());

  // Com o fundo VAMOO ligado o campo do menu mostra o código, que não é hex.
  const invalid = THEME_COLOR_KEYS.filter(key => !(vamoo && key === "rail") && normalizeHex(drafts[key]) === null);

  return (
    <section ref={sectionRef} className="card account-card custom-theme-editor" aria-labelledby="custom-theme-title">
      <div className="card-title">
        <div className="card-title-header">
          <Paintbrush className="card-icon" size={20} />
          <div>
            <h3 id="custom-theme-title">Seu Tema Personalizado</h3>
            <p>Escolha as cores e veja o sistema inteiro mudar na hora. Nada é salvo até você clicar em salvar.</p>
          </div>
        </div>
      </div>

      <div className="custom-theme-toolbar">
        <label className="custom-theme-start">
          <span>Começar de</span>
          <Select defaultValue="" onChange={event => startFrom(event.target.value)}>
            <option value="" disabled>Escolha um tema pronto…</option>
            {presets.map(preset => (
              <option key={preset.id} value={preset.id}>{preset.name}</option>
            ))}
          </Select>
        </label>
        <button type="button" className="secondary-button" onClick={() => replaceAll(generateFromAccent(colors.accent))}>
          <Wand2 size={15} aria-hidden="true" /> Gerar combinação a partir do destaque
        </button>
      </div>

      <div className="custom-theme-body">
        <div className="custom-theme-fields">
          {THEME_COLOR_KEYS.map(key => {
            const { label } = FIELDS[key];
            const help =
              key === "rail" && vamoo
                ? "Fundo VAMOO ligado: imagem no menu e logo VAMOO. Digite uma cor pra desligar"
                : FIELDS[key].help;
            const isInvalid = invalid.includes(key);
            return (
              <div key={key} className="custom-theme-field-group">
                <div className={`custom-theme-field ${isInvalid ? "is-invalid" : ""}`}>
                  <input
                    type="color"
                    className="custom-theme-swatch"
                    value={colors[key]}
                    aria-label={`${label}: escolher cor`}
                    onChange={event => setColor(key, event.target.value)}
                  />
                  <div className="custom-theme-field-copy">
                    <strong>{label}</strong>
                    <span>{help}</span>
                  </div>
                  <input
                    type="text"
                    className="custom-theme-hex"
                    value={drafts[key]}
                    maxLength={7}
                    spellCheck={false}
                    aria-label={`${label}: código hexadecimal`}
                    aria-invalid={isInvalid}
                    onChange={event => setColor(key, event.target.value)}
                    onBlur={() =>
                      setDrafts(prev => ({ ...prev, [key]: key === "rail" && vamoo ? VAMOO_UNLOCK_CODE : colors[key] }))
                    }
                  />
                </div>
                {key === "accent" && (
                  <div className="custom-theme-suggestions" role="group" aria-label="Sugestões de destaque">
                    {ACCENT_SUGGESTIONS.map(item => (
                      <button
                        key={item.hex}
                        type="button"
                        className={`custom-theme-suggestion ${colors.accent === item.hex ? "selected" : ""}`}
                        style={{ background: item.hex }}
                        title={item.name}
                        aria-label={`Destaque ${item.name}`}
                        aria-pressed={colors.accent === item.hex}
                        onClick={() => setColor("accent", item.hex)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="custom-theme-side">
          <ThemePreview colors={colors} railImage={vamoo ? VAMOO_RAIL_IMAGE : undefined} />
          {/* Amostras com os tokens REAIS (o app já está pintado com o
              rascunho): mostram a letra que o sistema escolheu sobre o
              destaque e o tom do destaque quando vira texto. */}
          <div className="custom-theme-sample">
            <span className="custom-theme-sample-button">Botão</span>
            <span className="custom-theme-sample-link">Link em destaque</span>
          </div>
          <div className="custom-theme-sample-cards">
            <span className="dashboard-summary-card--card1">1.204</span>
            <span className="dashboard-summary-card--card2">87%</span>
            <span className="dashboard-summary-card--card3">7,4</span>
          </div>
        </div>
      </div>

      {(warnings.length > 0 || darkerAccentText) && (
        <ul className="custom-theme-notes">
          {warnings.map(warning => (
            <li key={warning.message} className="is-warning">
              <AlertTriangle size={15} aria-hidden="true" />
              <span>{warning.message}</span>
            </li>
          ))}
          {darkerAccentText && (
            <li>
              <Info size={15} aria-hidden="true" />
              <span>O destaque é claro demais para texto sobre fundo branco: links e textos em destaque usam um tom mais escuro dele, automaticamente.</span>
            </li>
          )}
        </ul>
      )}

      <div className="card-actions">
        <button type="button" className="primary-button" disabled={invalid.length > 0} onClick={() => onSave(colors, vamoo)}>
          Salvar e usar este tema
        </button>
        <button type="button" className="secondary-button" onClick={onCancel}>
          Descartar
        </button>
      </div>
    </section>
  );
}
